import React, { useState } from 'react';
import {
  CurrentEngagementCandidateStatuses,
  InConsiderationCandidateStatuses,
  NoLongerInConsiderationCandidateStatuses,
  PreviousEngagementCandidateStatuses,
} from '@axiom/const';
import {
  Button,
  EmptyLayout,
  Gutter,
  Layout,
  Paragraph,
  ParagraphHeader,
  useApi,
} from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import NoResults from '../../public/svg/no-results-binoculars.svg';
import { CandidateOpportunityLegacyApi } from '../../api/candidate-opportunity-legacy-api';
import { useRefreshCandidate } from '../../hooks/useRefreshCandidate';
import { TalentOpportunityContainer } from '../TalentOpportunityContainer/TalentOpportunityContainer';
import { TalentOpportunityCardTypeNames } from '../TalentOpportunityCard/TalentOpportunityCard';
import { FindOpportunityForCandidateModal } from '../FindOpportunityForCandidateModal/FindOpportunityForCandidateModal';

import { TalentOpportunityTabFilter } from './TalentOpportunityTabFilter';

export const TalentOpportunityTab = ({
  candidate,
}: {
  candidate: Candidate;
}) => {
  const [{ data: candidateOpportunities }] = useApi(
    CandidateOpportunityLegacyApi.readCandidateOpportunities(candidate.id)
  );

  const [showFindOppModal, setShowFindOppModal] = useState(false);
  const { refreshCandidate } = useRefreshCandidate(candidate.id);
  const [filters, setFilters] = useState<string[]>([]);

  const filterByStatuses = (statuses: string[]) =>
    candidateOpportunities.filter(
      opp =>
        statuses.includes(opp.candidateStatus) &&
        (filters.length === 0 || filters.includes(opp.candidateStatus))
    );

  const inConsiderationOpps = filterByStatuses(
    InConsiderationCandidateStatuses
  );
  const currentEngagementOpps = filterByStatuses(
    CurrentEngagementCandidateStatuses
  );
  const previousEngagementOpps = filterByStatuses(
    PreviousEngagementCandidateStatuses
  );
  const noLongerInConsiderationOpps = filterByStatuses(
    NoLongerInConsiderationCandidateStatuses
  );

  return (
    <>
      <div data-test="TALENT_OPPORTUNITIES">
        <Gutter vertical="8px" />
        <Layout position="right">
          <Button
            name="OPP_ADD_BUTTON"
            pattern="secondary"
            variation="filled"
            onClick={() => {
              setShowFindOppModal(true);
            }}
          >
            Add Opportunities
          </Button>
        </Layout>
        {/* APCORE-2418 - Need to retain the old style DropdownTree for now because the new
          one goes up and out of view on smaller screens */}
        <TalentOpportunityTabFilter
          name="TALENT_OPP_TAB_FILTER"
          onFilterChange={setFilters}
        />
        <Gutter vertical="8px" />
        {(inConsiderationOpps.length > 0 || filters.length === 0) && (
          <TalentOpportunityContainer
            type={TalentOpportunityCardTypeNames.inConsideration}
            candidateOpportunities={inConsiderationOpps}
          />
        )}
        {(currentEngagementOpps.length > 0 || filters.length === 0) && (
          <TalentOpportunityContainer
            type={TalentOpportunityCardTypeNames.currentEngagements}
            candidateOpportunities={currentEngagementOpps}
            candidate={candidate}
          />
        )}
        {(previousEngagementOpps.length > 0 || filters.length === 0) && (
          <TalentOpportunityContainer
            type={TalentOpportunityCardTypeNames.previousEngagements}
            candidateOpportunities={previousEngagementOpps}
          />
        )}
        {(noLongerInConsiderationOpps.length > 0 || filters.length === 0) && (
          <TalentOpportunityContainer
            type={TalentOpportunityCardTypeNames.noLongerInConsideration}
            candidateOpportunities={noLongerInConsiderationOpps}
          />
        )}
        {filters.length > 0 &&
          ![
            inConsiderationOpps,
            currentEngagementOpps,
            previousEngagementOpps,
            noLongerInConsiderationOpps,
          ].some(arr => arr.length) && (
            <EmptyLayout
              graphic={<NoResults width="117px" height="117px" />}
              name="NO_OPPORUNTITIES_FOUND"
            >
              <Gutter bottom="16px" />
              <ParagraphHeader>No Opportunities Found</ParagraphHeader>
              <Gutter bottom="16px" />
              <Paragraph>
                No matching opportunities found. Try adding more filters or
                clearing your current selection of filters.
              </Paragraph>
            </EmptyLayout>
          )}
      </div>
      {showFindOppModal ? (
        <FindOpportunityForCandidateModal
          name="FIND_OPP_MODAL"
          onClose={() => {
            setShowFindOppModal(false);
          }}
          onSuccess={() => {
            void refreshCandidate(candidate.id);
          }}
          candidateIds={[candidate.id]}
          candidateName={candidate.calculatedDisplayName}
          reloadCandidateId={candidate.id}
        />
      ) : null}
    </>
  );
};
